import React from "react"
import { Typography, Switch, Space, Button, Row, Col, Card, Tooltip, Modal } from "antd"
import { InfoCircleOutlined, RiseOutlined } from "@ant-design/icons"
import SearchHistory from "./SearchHistory"
import JobBoardSearchForm from "./JobBoardSearchForm"
import cssStyles from './MonsterSearch.module.css'

const { Title, Text, Paragraph } = Typography

const fieldGroups = [
  {
    title: "Basic Search",
    fields: ["keyword", "keywordBehavior", "jobTitle", "targetJobTitle", "recentCompany", "recentJobDescription"]
  },
  {
    title: "Location & Work Site",
    fields: ["location", "radius", "targetWorkLocation", "targetWorkLocationBoolean", "targetWorkSite", "willingToRelocate"]
  },
  {
    title: "Experience & Education",
    fields: ["experience", "education", "educationMajor", "careerLevel", "managerialExperience", "projectLeadership"]
  },
  {
    title: "Job Details",
    fields: ["jobType", "industry", "targetIndustry", "salary"]
  },
  {
    title: "Additional Qualifications",
    fields: ["languageSkills", "resumeLanguage", "workAuthorization", "securityClearance", "militaryStatus"]
  },
  {
    title: "Search Options",
    fields: ["updatedWithin", "sortBy"]
  }
];

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #4834d4 0%, #686de0 100%)',
    padding: '40px 20px',
    overflowX: 'hidden',
  },
  backgroundPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.05,
    backgroundImage: 'radial-gradient(circle at 25px 25px, white 2%, transparent 0%), radial-gradient(circle at 75px 75px, white 2%, transparent 0%)',
    backgroundSize: '100px 100px',
    pointerEvents: 'none',
  },
  content: {
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    position: 'relative',
    zIndex: 1
  },
  header: {
    textAlign: 'center',
    marginBottom: '40px',
    color: 'white',
  },
  title: {
    fontSize: '3rem',
    fontWeight: 700,
    marginBottom: '16px',
    background: 'linear-gradient(45deg, #fff 30%, #e2e2e2 90%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  subtitle: {
    fontSize: '1.25rem',
    opacity: 0.9,
    maxWidth: '600px',
    margin: '0 auto',
  },
  searchCard: {
    background: 'rgba(255, 255, 255, 0.98)',
    borderRadius: '16px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
    padding: '32px',
    marginBottom: '32px',
    backdropFilter: 'blur(10px)',
  },
  searchOptions: {
    marginTop: '24px',
    padding: '16px 0',
    borderTop: '1px solid rgba(0, 0, 0, 0.06)',
  },
  switchOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  advancedButton: {
    height: '40px',
    borderRadius: '20px',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.3s ease',
  },
  historySection: {
    background: 'rgba(255, 255, 255, 0.98)',
    borderRadius: '16px',
    padding: '0',
    backdropFilter: 'blur(10px)',
    marginBottom: '32px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
  },
  advancedForm: {
    maxHeight: 'calc(80vh - 200px)',

    overflowY: 'auto',
    padding: '0 16px',
    marginTop: '16px',
  },
  formGroup: {
    marginBottom: '24px',
    background: '#f8f9fa',
    borderRadius: '8px',
    padding: '16px',
  },
  groupTitle: {
    fontSize: '16px',
    fontWeight: 500,
    marginBottom: '16px',
    color: '#1890ff',
    borderBottom: '1px solid #f0f0f0',
    paddingBottom: '8px',
  },
  fieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '16px',
  },
}

const SearchLanding = ({ 
  searchType, 
  onSearchTypeChange, 
  initialFields, 
  onSubmit, 
  searchParams, 
  handleOpenAdvanceOptions, 
  searchSuggestions,
  onFieldChange
}) => {
  return (
    <div style={styles.container} className={cssStyles.monsterSearchLanding}>
      <div style={styles.backgroundPattern} />

      <div style={styles.content}>
        <div style={styles.header}>
          <Title style={styles.title}>
            Find Your Perfect Candidate
          </Title>
          <Paragraph style={styles.subtitle}>
            Leverage our advanced AI-powered search to discover qualified candidates
            that perfectly match your requirements
          </Paragraph>
        </div>

        <Card style={styles.searchCard} className={cssStyles.searchCard}>
          <JobBoardSearchForm
            fields={initialFields}
            initialValues={searchParams}
            onSubmit={onSubmit}
            columns={3}
            formLayout="default"
            formItemClassName={cssStyles.formItem}
            onFieldChange={onFieldChange}
          />

          <div style={styles.searchOptions}>
            <Row justify="space-between" align="middle">
              <Col xs={24} sm={24} md={14}>
                <Space size="large" wrap>
                  <Tooltip title="Use AI-powered semantic matching to find candidates based on meaning, not just keywords">
                    <div style={styles.switchOption}>
                      <Switch
                        checked={searchType === "semantic"}
                        onChange={(checked) => onSearchTypeChange(checked ? "semantic" : "boolean")}
                        className={cssStyles.switch}
                      />
                      <Text>Semantic Search</Text>
                      <InfoCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
                    </div>
                  </Tooltip>

                  <Tooltip title="Use traditional keyword matching with boolean operators">
                    <div style={styles.switchOption}>
                      <Switch
                        checked={searchType === "boolean"}
                        onChange={(checked) => onSearchTypeChange(checked ? "boolean" : "semantic")}
                        className={cssStyles.switch}
                      />
                      <Text>Boolean Search</Text>
                      <InfoCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
                    </div>
                  </Tooltip>
                </Space>
              </Col>

              <Col xs={24} sm={24} md={10} style={{ textAlign: 'right', marginTop: { xs: '16px', md: 0 } }}>
                <Button
                  type="primary"
                  onClick={() => {
    
                    const formValues = {};
                    
            
                    const keywordInput = document.querySelector('input[name="keyword"]');
                    if (keywordInput) {
            
                      formValues.keyword = keywordInput.value;
                    }
                    
                  
                    try {
                      const savedValues = localStorage.getItem('monster_search_values');
                      if (savedValues) {
                        const parsedValues = JSON.parse(savedValues);
                     
                        Object.entries(parsedValues).forEach(([key, value]) => {
                          if (!formValues[key] && value) {
                            formValues[key] = value;
                          }
                        });
                      }
                    } catch (error) {
                      console.error('Error reading from localStorage:', error);
                    }
                    
    
                    ['location', 'radius', 'jobTitle', 'targetJobTitle'].forEach(fieldName => {
                      const input = document.querySelector(`input[name="${fieldName}"], select[name="${fieldName}"]`);
                      if (input && input.value) {
                        formValues[fieldName] = input.value;
                      }
                    });
                    
            
                    try {
                      localStorage.setItem('monster_search_values', JSON.stringify(formValues));
                    } catch (error) {
                      console.error('Error saving to localStorage:', error);
                    }
                    
       
                    if (!formValues.radius) {
                      formValues.radius = "30";
                    }
                    
                    handleOpenAdvanceOptions(formValues);
                  }}
                  style={styles.advancedButton}
                  icon={<RiseOutlined />}
                  className={cssStyles.advancedButton}
                >
                  Advanced Search Options
                </Button>
              </Col>
            </Row>
          </div>
        </Card>

        <div style={styles.historySection} className={cssStyles.searchHistoryContainer}>
          <SearchHistory onSearch={onSubmit} searchSuggestions={searchSuggestions} />
        </div>
      </div>
    </div>
  )
}

export default SearchLanding

